import { rejectCandidateSchema } from "@vidario/domain";
import { Button, Modal, Textarea } from "@vidario/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { type ReactElement } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

type RejectForm = z.infer<typeof rejectCandidateSchema>;

export interface RejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;
}

/** MVP §4.8: Ablehnen requires a pflicht-commentary. The reason is
 * persisted to `candidates.rejection_reason` and echoed on the
 * rejected candidate's card in the Longlist (dimmed row). */
export function RejectDialog({
  open,
  onOpenChange,
  onConfirm,
  loading,
}: RejectDialogProps): ReactElement {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<RejectForm>({
    resolver: zodResolver(rejectCandidateSchema),
    defaultValues: { reason: "" },
  });

  const submit = (values: RejectForm): void => {
    onConfirm(values.reason);
    reset();
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Kandidat ablehnen"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            variant="destructive"
            loading={loading || isSubmitting}
            onClick={() => void handleSubmit(submit)()}
          >
            Ablehnen
          </Button>
        </>
      }
    >
      <p className="mb-4 mt-0 text-sm text-text-secondary">
        Der Ablehnungsgrund ist intern nachvollziehbar und hilft uns, das Matching zu verbessern.
      </p>
      <form onSubmit={(e) => void handleSubmit(submit)(e)} noValidate>
        <Textarea
          label="Ablehnungsgrund"
          required
          rows={4}
          placeholder="z. B. Seniority passt nicht, Gehalt außerhalb der Spanne…"
          {...register("reason")}
          {...(errors.reason?.message && { error: errors.reason.message })}
        />
      </form>
    </Modal>
  );
}
